import type { IconDefinition } from "@fortawesome/pro-regular-svg-icons";
import {
  faArrowLeft,
  faBan,
  faBolt,
  faCheck,
  faClock,
  faPaperPlaneTop,
  faPlug,
  faPlus,
  faTrash,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import {
  Badge,
  Button,
  Grid,
  Icon,
  Inline,
  Message,
  Modal,
  Section,
  Tooltip,
} from "@intility/bifrost-react";
import { useQueries } from "@tanstack/react-query";
import { type ComponentProps, useState } from "react";
import {
  clientAudienceGrantsQuery,
  useAddAudienceGrant,
  useCancelRequest,
  useClientAudienceGrants,
  useClientRequests,
  useRemoveAudienceGrant,
} from "../api/access";
import { useClients } from "../api/clients";
import { usePlatform } from "../api/platforms";
import type { ClientListItem } from "../api/types";
import {
  AddPermissionDrawer,
  type Notice,
} from "../pages/clients/ClientDetail/ClientAccessSection";
import { ClientPurposeBadge } from "./ClientTypeBadge";
import { useCanEditClient } from "./useCanEditClient";
import "./ClientApiAccess.css";

type BadgeState = ComponentProps<typeof Badge>["state"];

// The pill on the API's box: where this client's access to the API stands.
// "incoming" is an API that can call this client, not the other way round.
export const STATUS: Record<
  string,
  { label: string; icon: IconDefinition; state: BadgeState }
> = {
  approved: { label: "Granted", icon: faCheck, state: "success" },
  pending: { label: "Pending approval", icon: faClock, state: "warning" },
  denied: { label: "Denied", icon: faBan, state: "alert" },
  cancelled: { label: "Cancelled", icon: faBan, state: "neutral" },
  incoming: { label: "Calls this client", icon: faArrowLeft, state: "brand" },
};

interface ApiLink {
  key: string;
  api: string;
  /** This client's access to the API; null when it only goes the other way. */
  status: string | null;
  /** The API has access to this client. */
  reverse: boolean;
  onRevoke?: () => void;
  onCancel?: () => void;
}

// The APIs this client may call, drawn as an arrow from the client to each API:
// a moving glow while the access is granted, dashed while it waits for
// approval. An API that has access to this client gets an arrow back.
export function ClientApiAccess({ client }: { client: ClientListItem }) {
  const canEdit = useCanEditClient(client);
  const { data: audienceGrants = [] } = useClientAudienceGrants(client.id);
  const { data: requests = [] } = useClientRequests(client.id);
  const audienceGrant = useAddAudienceGrant(client.id);
  const removeAudience = useRemoveAudienceGrant(client.id);
  const cancel = useCancelRequest(client.id);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<string | null>(null);

  // A SPA can be granted the other API clients of its platform in one go.
  const { data: platform } = usePlatform(
    client.appType === "spa" && client.platformId ? client.platformId : "",
  );
  const granted = new Set(audienceGrants.map((g) => g.audience));
  const awaiting = new Set(
    requests
      .filter((r) => r.requestType === "api-access" && r.status === "pending")
      .map((r) => r.target),
  );
  const siblingApiClientIds = (platform?.clients ?? [])
    .filter((c) => c.appType === "api" && c.id !== client.id)
    .map((c) => c.clientId);
  const missing = siblingApiClientIds.filter(
    (id) => !granted.has(id) && !awaiting.has(id),
  );

  async function grantMissing() {
    setNotice(null);
    let done = 0;
    let queued = 0;
    let failed = 0;
    for (const audience of missing) {
      try {
        const result = await audienceGrant.mutateAsync({ audience });
        if (result.requiresApproval) queued++;
        else done++;
      } catch {
        failed++;
      }
    }
    const parts: string[] = [];
    if (done) parts.push(`${done} granted`);
    if (queued) parts.push(`${queued} queued for approval`);
    if (failed) parts.push(`${failed} failed`);
    setNotice({
      state: failed ? "warning" : "success",
      text: `API access: ${parts.join(", ")}.`,
    });
  }

  // Which APIs have access to this client: the audience grants of every API we
  // know of here, the ones we call and the API clients of our platform.
  const { data: allClients = [] } = useClients();
  const { data: ownPlatform } = usePlatform(client.platformId ?? "");
  const outgoing = [
    ...audienceGrants.map((g) => g.audience),
    ...requests
      .filter((r) => r.requestType === "api-access")
      .map((r) => r.target),
  ];
  const candidates = [
    ...new Set([
      ...outgoing,
      ...(ownPlatform?.clients ?? [])
        .filter((c) => c.appType === "api" && c.id !== client.id)
        .map((c) => c.clientId),
    ]),
  ].flatMap((apiClientId) => {
    const api = allClients.find((c) => c.clientId === apiClientId);
    return api && api.id !== client.id ? [{ apiClientId, id: api.id }] : [];
  });
  const incoming = useQueries({
    queries: candidates.map((c) => clientAudienceGrantsQuery(c.id)),
  });
  const callsUs = new Set(
    candidates
      .filter((_, i) =>
        incoming[i]?.data?.some((g) => g.audience === client.clientId),
      )
      .map((c) => c.apiClientId),
  );

  const links: ApiLink[] = [
    ...audienceGrants.map((g) => ({
      key: `aud:${g.audience}`,
      api: g.audience,
      status: "approved",
      reverse: callsUs.has(g.audience),
      onRevoke: () => setPendingRemoval(g.audience),
    })),
    ...requests
      .filter((r) => r.requestType === "api-access" && r.status !== "approved")
      .map((r) => ({
        key: `req:${r.id}`,
        api: r.target,
        status: r.status,
        reverse: callsUs.has(r.target),
        onCancel:
          r.status === "pending" ? () => cancel.mutate(r.id) : undefined,
      })),
    // APIs that can call this client without it being able to call them.
    ...[...callsUs]
      .filter((api) => !outgoing.includes(api))
      .map((api) => ({
        key: `in:${api}`,
        api,
        status: null,
        reverse: true,
      })),
  ];

  const error = removeAudience.error ?? cancel.error;

  return (
    <Section border radius background="base-3" className="client-api-access">
      <Section.Header>
        <Inline align="center">
          <Icon icon={faPlug} />
          <Inline.Stretch>API access</Inline.Stretch>
          <Tooltip content="Add API access">
            <Button
              variant="flat"
              small
              className="client-icon-button"
              aria-label="Add API access"
              disabled={!canEdit}
              onClick={() => {
                setNotice(null);
                setDrawerOpen(true);
              }}
            >
              <Icon icon={faPlus} />
            </Button>
          </Tooltip>
        </Inline>
      </Section.Header>
      <Section.Content className="client-api-access-body">
        <p className="bf-p bfc-base-2" style={{ margin: 0 }}>
          APIs this client can call
        </p>

        {notice && <Message state={notice.state}>{notice.text}</Message>}
        {error && (
          <Message state="alert" header="Could not update API access">
            {error instanceof Error ? error.message : "Something went wrong."}
          </Message>
        )}

        {missing.length > 0 && (
          <Message state="neutral">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 16,
              }}
            >
              <span>
                <Icon icon={faBolt} marginRight />
                Grant this client access to{" "}
                {missing.length === 1 ? (
                  <strong>{missing[0]}</strong>
                ) : (
                  <>
                    <strong>{missing.length}</strong> APIs in this platform
                  </>
                )}
                ?
              </span>
              <Tooltip
                content={audienceGrant.isPending ? "Applying…" : "Apply"}
              >
                <Button
                  variant="flat"
                  small
                  className="client-icon-button"
                  aria-label="Apply"
                  onClick={grantMissing}
                  disabled={!canEdit || audienceGrant.isPending}
                >
                  <Icon icon={faCheck} />
                </Button>
              </Tooltip>
            </div>
          </Message>
        )}

        {links.length === 0 ? (
          <p
            className="bf-p bfc-base-2"
            style={{ margin: 0, fontStyle: "italic" }}
          >
            No API access yet
          </p>
        ) : (
          <ul className="client-api-links">
            {links.map((link) => {
              const apiClient = allClients.find((c) => c.clientId === link.api);
              return (
                <li key={link.key} className="client-api-link">
                  <div className="client-api-node">
                    <ClientPurposeBadge
                      appType={client.appType}
                      purposeTag={client.purposeTag}
                    />
                    <span className="client-api-node-name">{client.name}</span>
                  </div>
                  <div className="client-api-connector">
                    {link.status === "pending" || link.status === "denied" ? (
                      // A request not connected: a line from the client that
                      // stops at a badge, grey with a paper plane while it waits
                      // for an answer, red with an x once it is denied.
                      <div
                        className="client-api-arrow"
                        data-status={link.status}
                      >
                        <span className="client-api-arrow-line" />
                        <Tooltip
                          content={
                            link.status === "denied"
                              ? "Request denied"
                              : "Request pending"
                          }
                        >
                          <Badge
                            state={
                              link.status === "denied" ? "alert" : "neutral"
                            }
                            className="client-api-pending-badge"
                            aria-label={
                              link.status === "denied"
                                ? "Request denied"
                                : "Request pending"
                            }
                          >
                            <Icon
                              icon={
                                link.status === "denied"
                                  ? faXmark
                                  : faPaperPlaneTop
                              }
                            />
                          </Badge>
                        </Tooltip>
                      </div>
                    ) : (
                      <>
                        {/* Two lines: the top one carries the glow to the API,
                        the bottom one back, when the API can call this
                        client too. */}
                        <div
                          className="client-api-arrow"
                          data-status={
                            !link.status
                              ? "none"
                              : link.status === "approved"
                                ? "granted"
                                : "waiting"
                          }
                        >
                          <span className="client-api-arrow-line" />
                        </div>
                        <div
                          className="client-api-arrow client-api-arrow-back"
                          data-status={link.reverse ? "granted" : "none"}
                        >
                          <span className="client-api-arrow-line" />
                        </div>
                      </>
                    )}
                  </div>
                  <div className="client-api-node client-api-node-api">
                    <ClientPurposeBadge
                      appType={apiClient?.appType ?? "api"}
                      purposeTag={apiClient?.purposeTag}
                    />
                    <span className="client-api-node-name">{link.api}</span>
                    {/* Granted, pending and denied say it with the connector;
                    the pill is for the rest. */}
                    {link.status !== "approved" &&
                      link.status !== "pending" &&
                      link.status !== "denied" && (
                        <Badge
                          state={STATUS[link.status ?? "incoming"].state}
                          className="client-api-status"
                        >
                          <Icon
                            icon={STATUS[link.status ?? "incoming"].icon}
                            marginRight
                          />
                          {STATUS[link.status ?? "incoming"].label}
                        </Badge>
                      )}
                    {link.onRevoke && (
                      <Tooltip content="Revoke access">
                        <Button
                          variant="flat"
                          state="alert"
                          small
                          className="client-api-revoke"
                          aria-label={`Revoke access to ${link.api}`}
                          disabled={!canEdit}
                          onClick={link.onRevoke}
                        >
                          <Icon icon={faTrash} />
                        </Button>
                      </Tooltip>
                    )}
                    {link.onCancel && (
                      <Tooltip content="Cancel request">
                        <Button
                          variant="flat"
                          state="alert"
                          small
                          className="client-api-revoke"
                          aria-label={`Cancel request for ${link.api}`}
                          disabled={!canEdit}
                          onClick={link.onCancel}
                        >
                          <Icon icon={faTrash} />
                        </Button>
                      </Tooltip>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <AddPermissionDrawer
          clientId={client.id}
          isSpa={client.appType === "spa"}
          mode={drawerOpen ? "api-access" : null}
          siblingApiClientIds={siblingApiClientIds}
          onClose={() => setDrawerOpen(false)}
          onResult={(n) => {
            setNotice(n);
            setDrawerOpen(false);
          }}
        />

        <Modal
          isOpen={pendingRemoval !== null}
          onRequestClose={() => setPendingRemoval(null)}
          header="Remove API access"
          width={480}
        >
          <Grid gap={16}>
            <p className="bf-p" style={{ margin: 0 }}>
              Remove access to <strong>{pendingRemoval}</strong>? It might need
              approval again if you add it back.
            </p>
            <div
              style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}
            >
              <Button variant="flat" onClick={() => setPendingRemoval(null)}>
                Cancel
              </Button>
              <Button
                variant="filled"
                state="alert"
                onClick={() => {
                  if (pendingRemoval) removeAudience.mutate(pendingRemoval);
                  setPendingRemoval(null);
                }}
              >
                Remove
              </Button>
            </div>
          </Grid>
        </Modal>
      </Section.Content>
    </Section>
  );
}
